import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { authAPI } from '../services/api'
import { getLoginFingerprint } from '../utils/deviceFingerprint'

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  otpStep: null,
  otpData: null,
}

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  LOAD_USER: 'LOAD_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
}

// Reducer
const authReducer = (state, action) => {
  console.log('🔐 AuthReducer: Action dispatched:', action.type, action.payload)
  console.log('🔐 AuthReducer: Current state:', {
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    hasUser: !!state.user
  })

  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      }

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        otpStep: null,
        otpData: null,
      }

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        otpStep: null,
        otpData: null,
      }
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        otpStep: null,
        otpData: null,
      }
    
    case AUTH_ACTIONS.LOAD_USER:
      const newState = {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      }
      console.log('🔐 AuthReducer: LOAD_USER - New state:', {
        isAuthenticated: newState.isAuthenticated,
        isLoading: newState.isLoading,
        hasUser: !!newState.user,
        hasToken: !!newState.token
      })
      return newState
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      }
    
    default:
      return state
  }
}

// Create context
const AuthContext = createContext()

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Load user from localStorage on app start
  useEffect(() => {
    console.log('🔐 AuthContext: Checking localStorage for existing auth...')
    const token = localStorage.getItem('token')
    const user = localStorage.getItem('user')
    const tokenExpiry = localStorage.getItem('tokenExpiry')

    console.log('🔐 AuthContext: Found in localStorage:', {
      hasToken: !!token,
      hasUser: !!user,
      tokenExpiry: tokenExpiry ? new Date(parseInt(tokenExpiry)).toISOString() : null
    })

    if (token && user) {
      try {
        // Check if token is expired
        if (tokenExpiry && new Date().getTime() > parseInt(tokenExpiry)) {
          console.log('🔐 AuthContext: Token expired, clearing localStorage')
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          localStorage.removeItem('tokenExpiry')
          dispatch({ type: AUTH_ACTIONS.LOGOUT })
          return
        }

        const parsedUser = JSON.parse(user)
        console.log('🔐 AuthContext: Auto-login successful for user:', parsedUser.mobile || parsedUser.email)
        dispatch({
          type: AUTH_ACTIONS.LOAD_USER,
          payload: { user: parsedUser, token }
        })
      } catch (error) {
        console.error('🔐 AuthContext: Error parsing user from localStorage:', error)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('tokenExpiry')
        dispatch({ type: AUTH_ACTIONS.LOGOUT })
      }
    } else {
      // No token found, set loading to false
      console.log('🔐 AuthContext: No token found, user not authenticated')
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
    }
  }, [])

  const login = async (email, password, location = null) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START })
    try {
      // Generate device fingerprint
      const deviceFingerprint = getLoginFingerprint()

      const response = await authAPI.login(email, password, deviceFingerprint)
      const data = response.data
      
      // Check if OTP is required (two-phase authentication)
      if (data.requiresOTP) {
        dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: '' }) // Clear any previous errors
        return {
          success: false,
          requiresOTP: true,
          mobile: data.mobile || null,
          smsSent: data.smsSent || false,
          hasMobile: data.hasMobile || false,
          message: data.message || 'OTP sent to your mobile number'
        }
      }

      // If no OTP required, complete login (backward compatibility)
      const expiryTime = new Date().getTime() + (7 * 24 * 60 * 60 * 1000)
      localStorage.setItem('token', data.data.token)
      localStorage.setItem('user', JSON.stringify(data.data.user))
      localStorage.setItem('tokenExpiry', expiryTime.toString())
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: { user: data.data.user, token: data.data.token } })

      // Return additional device info
      return {
        success: true,
        data: data.data,
        device: data.data.device,
        isNewDevice: data.data.isNewDevice,
        requiresDeviceConfirmation: data.data.requiresDeviceConfirmation
      }
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message || 'Login failed'
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const verifyOTP = async (email, otp, location = null) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START })
    try {
      // Generate device fingerprint
      const deviceFingerprint = getLoginFingerprint()

      const response = await authAPI.verifyOTPLogin(email, otp, location, deviceFingerprint)
      const data = response.data
      const expiryTime = new Date().getTime() + (7 * 24 * 60 * 60 * 1000)
      localStorage.setItem('token', data.data.token)
      localStorage.setItem('user', JSON.stringify(data.data.user))
      localStorage.setItem('tokenExpiry', expiryTime.toString())
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: { user: data.data.user, token: data.data.token } })

      // Return additional device info
      return {
        success: true,
        data: data.data,
        device: data.data.device,
        isNewDevice: data.data.isNewDevice,
        requiresDeviceConfirmation: data.data.requiresDeviceConfirmation
      }
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.response?.data?.detail || e.message || 'OTP verification failed'
      const errorData = e.response?.data
      
      // Extract remaining attempts if available
      let remainingAttempts = 5
      if (errorData?.details?.remainingAttempts !== undefined) {
        remainingAttempts = errorData.details.remainingAttempts
      } else if (errorData?.remainingAttempts !== undefined) {
        remainingAttempts = errorData.remainingAttempts
      } else if (errorMessage.includes('attempts')) {
        // Try to extract from error message
        const match = errorMessage.match(/(\d+)\s+attempts?/i)
        if (match) {
          remainingAttempts = parseInt(match[1])
        }
      }
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage })
      return { 
        success: false, 
        error: errorMessage,
        remainingAttempts: remainingAttempts
      }
    }
  }



  // Logout function
  const logout = async () => {
    try {
      // Call logout API to invalidate token on server
      await authAPI.logout()
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error)
    } finally {
      // Always clear local storage and state
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('tokenExpiry')
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
    }
  }

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  }

  // Development helper: Set a mock token for testing
  const setDevelopmentToken = () => {
    const mockUser = {
      id: 1,
      mobile: '2345012345',
      email: 'admin@videoeditor.com',
      firstName: 'Admin',
      lastName: 'User',
      role: 'ADMIN'
    }
    const mockToken = 'dev-token-' + Date.now()
    const expiryTime = new Date().getTime() + (7 * 24 * 60 * 60 * 1000)

    localStorage.setItem('token', mockToken)
    localStorage.setItem('user', JSON.stringify(mockUser))
    localStorage.setItem('tokenExpiry', expiryTime.toString())

    dispatch({
      type: AUTH_ACTIONS.LOAD_USER,
      payload: { user: mockUser, token: mockToken }
    })

    console.log('🔐 Development token set for user:', mockUser.mobile)
  }

  // Update user in context and localStorage (e.g., after profile save)
  const updateUser = (partialUser) => {
    try {
      const currentToken = localStorage.getItem('token') || state.token
      const existing = localStorage.getItem('user')
      const existingUser = existing ? JSON.parse(existing) : state.user || {}
      const merged = { ...existingUser, ...partialUser }
      localStorage.setItem('user', JSON.stringify(merged))
      dispatch({ type: AUTH_ACTIONS.LOAD_USER, payload: { user: merged, token: currentToken } })
    } catch (e) {
      console.error('Failed updating user in context', e)
    }
  }

  const value = {
    ...state,
    login,
    verifyOTP,
    logout,
    clearError,
    setDevelopmentToken,
    updateUser,
  }

  // Debug the context value being provided
  console.log('🔐 AuthProvider: Context value:', {
    isAuthenticated: value.isAuthenticated,
    isLoading: value.isLoading,
    hasUser: !!value.user,
    hasToken: !!value.token
  })

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
